import {
    Component,
    effect,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    signal,
    SimpleChanges
} from '@angular/core';
import { CodeList } from '@common/classes/codelist';
import { defaultSelectLabel } from '@common/models/util';
import { CodelistPipe } from '@common/pipes/codelist.pipe';
import { CommonService } from '@common/services/common.service';
import { QueryService } from '@common/services/query.service';
import { environment } from '@environments/environment';
import { tapSuccessResult } from '@ngneat/query';
import { debounce } from 'lodash';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';
import { BaseInputComponent } from '../base-input/base-input.component';
import { CodelistInputConfig } from '../input.type';

/**
 * Codelist input component.
 * @param codelist - The codelist name.
 * @param isMultiple - Whether the input is multiple.
 * @param textField - The text field of the codelist.
 * @param valueField - The value field of the codelist.
 * @param popupSettings - The popup settings of the dropdown.
 * @param formatLabel - The function to format the label.
 * @param customFilter - The custom filter function.
 */
@Component({
    selector: 'app-codelist-input',
    templateUrl: './codelist-input.component.html',
    providers: [{ provide: BaseInputComponent, useExisting: CodelistInputComponent }]
})
export class CodelistInputComponent
    extends BaseInputComponent<string | string[] | number | number[]>
    implements OnInit, OnDestroy, OnChanges
{
    @Input({ required: true }) codelist: CodelistInputConfig['codelist'];
    @Input() isMultiple: CodelistInputConfig['isMultiple'];
    @Input() maxLength: CodelistInputConfig['maxLength'];
    @Input() codelistFull: CodelistInputConfig['codelistFull'] = false;
    @Input() textField: CodelistInputConfig['textField'] = 'label';
    @Input() valueField: CodelistInputConfig['valueField'] = 'value';
    @Input() popupSettings: CodelistInputConfig['popupSettings'] =
        environment.settings.appControl.dropdown.popupSettings;
    @Input() omittedCodelistIds: CodelistInputConfig['omittedCodelistIds'];
    @Input() itemDisabled: CodelistInputConfig['itemDisabled'] = () => false;
    @Input() formatLabel: CodelistInputConfig['formatLabel'] = (item: CodeList) => defaultSelectLabel(item);
    @Input() customFilter: CodelistInputConfig['customFilter'];
    @Input() customBackendFilter: CodelistInputConfig['customBackendFilter'];

    @HostBinding('class') hostClass = 'truncate';

    query$;
    options = signal<{ value: string; label: string; [key: string]: any }[]>([]);
    filteredOptions = signal<{ value: string; label: string; [key: string]: any }[]>([]);
    readonly codelistTake = environment.settings.appControl.codelist.take;
    formatedSelectedOptions = '';
    private destroy$ = new Subject<boolean>();
    private isEnsuringSeletedValue = false;

    constructor(
        private queryService: QueryService,
        private codelistPipe: CodelistPipe,
        private commonService: CommonService
    ) {
        super();
        effect(async () => {
            this.formatedSelectedOptions = await this.formatSelectedOptions(this.value());
            this.ensureSelectedValueIsAvailable();
        });
    }

    /**
     * WORKAROUND: Ensures the currently selected value is available in the options list.
     *
     * This is a band-aid fix for a timing issue in the component initialization lifecycle:
     * 1. ngOnInit() calls queryCodelist('') before parent component binds the value
     * 2. Initial query has selectedIds: null, so backend doesn't return selected item data
     * 3. When parent later binds the value, the selected item is missing from options
     * 4. This causes dropdowns to appear empty even though the value exists
     *
     * IDEAL SOLUTION: Fix the root cause by ensuring proper reactive data flow between
     * parent components and codelist inputs, or restructuring the initialization order.
     * This workaround detects missing selected values and re-queries to include them.
     *
     * Occurs specifically when:
     * - Navigating directly to edit mode (e.g., after creating credit notes)
     * - Refreshing page while in edit mode
     * - Any scenario where component initializes in edit mode with pre-existing values
     */
    private ensureSelectedValueIsAvailable(): void {
        const currentValue = this.value();
        const hasValue = this.isMultiple
            ? Array.isArray(currentValue) && currentValue.length > 0
            : currentValue !== null && currentValue !== undefined;

        if (!hasValue || this.options().length === 0 || this.isEnsuringSeletedValue) return;

        const selectedExists = this.options().some((option) =>
            this.isMultiple ? [this.value()].flat().includes(option.value) : option.value === this.value()
        );

        if (!selectedExists) {
            this.isEnsuringSeletedValue = true;
            this.queryCodelist('');
            // Reset the flag after a short delay to allow for future checks
            setTimeout(() => {
                this.isEnsuringSeletedValue = false;
            }, 100);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['customFilter']) {
            this.queryCodelist('');
        }
    }

    ngOnInit(): void {
        this.queryCodelist('');
        if (!this.maxLength) return;

        this.itemDisabled = (itemArgs: { dataItem: any; index: number }) => {
            const raw = this.value();
            if (!Array.isArray(raw)) return false;
            const val = raw as Array<string | number>;
            return !val.includes(itemArgs.dataItem.value) && val.length >= this.maxLength;
        };
    }

    ngOnDestroy() {
        this.destroy$.next(null);
        this.destroy$.complete();
    }

    queryCodelist(filter) {
        this.query$ = this.queryService.getCodelistQuery({
            customFilter: this.customBackendFilter,
            name: this.codelist,
            filter,
            selectedIds: this.value() ? [this.value()].flat() : null,
            omittedIds: this.omittedCodelistIds,
            injector: this.commonService.injector
        }).result$;
        this.query$
            .pipe(
                tapSuccessResult((data: []) => {
                    this.options.set(
                        data.map((x: any) => ({
                            label: this.formatLabel(x),
                            value: x.id,
                            name: x.name
                        }))
                    );
                    if (this.options().length === 1 && !this.value() && !filter && this.isRequired)
                        this.setValue(this.options().at(0).value);

                    const currentValue = this.isMultiple ? this.value()?.[0] : this.value();
                    if (this.customFilter) {
                        this.filteredOptions.set(this.options().filter((x: any) => this.customFilter(x, filter)));
                        return;
                    }
                    this.filteredOptions.set(
                        this.options().filter((x, i) => x.value === currentValue || i < this.codelistTake + 1)
                    );
                }),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    queryCodelistDebounced = debounce((filter) => this.queryCodelist(filter), 500);

    async formatSelectedOptions(selected: string | string[] | number | number[]): Promise<string> {
        if (!this.options() || !selected) return null;

        if (this.isMultiple) {
            if (!Array.isArray(selected) || selected.length === 0) {
                return null;
            }
            return await Promise.all(
                selected.map(
                    async (item) => await lastValueFrom(this.codelistPipe.transform(item, this.codelist, false))
                )
            ).then((res) => res.join(', '));
        } else {
            if (Array.isArray(selected)) return null;
            return await lastValueFrom(this.codelistPipe.transform(selected, this.codelist, this.codelistFull));
        }
    }
}
